import { useState, useEffect } from 'react';
import { Search, Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import Layout from '../Layout';
import LibraryCard from './LibraryCard';
import LibraryDetailModal from './LibraryDetailModal';

const selectClasses = (isDark) =>
  `px-3 py-2 rounded-lg text-sm outline-none transition-colors duration-fast focus:ring-2 ${
    isDark
      ? 'bg-background-dark border border-border-dark text-gray-300 focus:border-primary-dark focus:ring-primary-dark/30'
      : 'bg-white border border-border text-text focus:border-primary focus:ring-primary/20'
  }`;

function SkeletonCard({ isDark }) {
  const shimmer = isDark ? 'bg-border-dark/60' : 'bg-background-sidebar';
  return (
    <div
      className={`rounded-lg overflow-hidden border animate-pulse ${
        isDark ? 'bg-background-dark/50 border-border-dark' : 'bg-white border-border'
      }`}
    >
      <div className={`w-full aspect-[4/3] ${shimmer}`} />
      <div className="p-4 space-y-3">
        <div className={`h-5 w-2/3 rounded ${shimmer}`} />
        <div className="flex gap-1.5">
          <div className={`h-5 w-14 rounded ${shimmer}`} />
          <div className={`h-5 w-16 rounded ${shimmer}`} />
        </div>
        <div className={`h-4 w-1/3 rounded ${shimmer}`} />
      </div>
    </div>
  );
}

export default function LibraryPage() {
  const { isDark } = useTheme();
  const [inspirations, setInspirations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDesignType, setFilterDesignType] = useState('');
  const [filterNiche, setFilterNiche] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [openInspiration, setOpenInspiration] = useState(null);

  const [designTypes, setDesignTypes] = useState([]);
  const [niches, setNiches] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchInspirations();
  }, []);

  const fetchInspirations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/library/get');
      const data = await response.json();

      if (data.success) {
        setInspirations(data.inspirations);

        const allDesignTypes = new Set();
        const allNiches = new Set();
        const allCategories = new Set();

        data.inspirations.forEach((insp) => {
          insp.design_types?.forEach((dt) => allDesignTypes.add(dt));
          insp.niches?.forEach((n) => allNiches.add(n));
          if (insp.category) allCategories.add(insp.category);
        });

        setDesignTypes(Array.from(allDesignTypes).sort());
        setNiches(Array.from(allNiches).sort());
        setCategories(Array.from(allCategories).sort());
      } else {
        setError(data.error || 'Failed to load library');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredInspirations = inspirations.filter((insp) => {
    const matchesSearch =
      searchQuery === '' ||
      insp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      insp.tags?.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesDesignType = filterDesignType === '' || insp.design_types?.includes(filterDesignType);
    const matchesNiche = filterNiche === '' || insp.niches?.includes(filterNiche);
    const matchesCategory = filterCategory === '' || insp.category === filterCategory;

    return matchesSearch && matchesDesignType && matchesNiche && matchesCategory;
  });

  return (
    <Layout hideRightSidebar compactSidebar>
      {() => (
        <div className="flex flex-col items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-12">
          <div className="w-full max-w-6xl">
            {/* Header */}
            <header className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles size={28} className={isDark ? 'text-primary-dark' : 'text-primary'} />
                <h1 className={`text-4xl font-display font-semibold ${isDark ? 'text-white' : 'text-text'}`}>
                  Inspiration Library
                </h1>
              </div>
              <p className={`text-base ${isDark ? 'text-gray-400' : 'text-text-muted'}`}>
                Design references, animations, and interactions that inspire my work
              </p>
            </header>

            {/* Search & Filters */}
            <div className="mb-12 space-y-4">
              <div className="relative">
                <Search
                  size={14}
                  className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-text-muted'}`}
                />
                <input
                  type="text"
                  placeholder="Search by title or tag..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-9 pr-3 py-2 rounded-lg text-sm outline-none transition-colors duration-fast focus:ring-2 ${
                    isDark
                      ? 'bg-background-dark text-gray-300 border border-border-dark placeholder-gray-600 focus:border-primary-dark focus:ring-primary-dark/30'
                      : 'bg-white text-text border border-border/30 placeholder-text-muted/60 focus:border-primary focus:ring-primary/20'
                  }`}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className={selectClasses(isDark)}>
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>

                <select value={filterDesignType} onChange={(e) => setFilterDesignType(e.target.value)} className={selectClasses(isDark)}>
                  <option value="">All Design Types</option>
                  {designTypes.map((dt) => (
                    <option key={dt} value={dt}>
                      {dt}
                    </option>
                  ))}
                </select>

                <select value={filterNiche} onChange={(e) => setFilterNiche(e.target.value)} className={selectClasses(isDark)}>
                  <option value="">All Niches</option>
                  {niches.map((niche) => (
                    <option key={niche} value={niche}>
                      {niche}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className={`mb-8 text-sm ${isDark ? 'text-gray-400' : 'text-text-muted'}`}>
              {loading ? 'Loading inspiration library...' : `${filteredInspirations.length} inspiration${filteredInspirations.length !== 1 ? 's' : ''}`}
            </div>

            {/* Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <SkeletonCard key={i} isDark={isDark} />
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <p className={isDark ? 'text-gray-400' : 'text-text-muted'}>Could not load the library: {error}</p>
              </div>
            ) : filteredInspirations.length === 0 ? (
              <div className="text-center py-16">
                <p className={isDark ? 'text-gray-400' : 'text-text-muted'}>No inspirations found. Try adjusting your filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredInspirations.map((inspiration, index) => (
                  <LibraryCard
                    key={inspiration.id}
                    inspiration={inspiration}
                    isDark={isDark}
                    position={index + 1}
                    total={filteredInspirations.length}
                    onOpen={() => setOpenInspiration(inspiration)}
                  />
                ))}
              </div>
            )}
          </div>

          {openInspiration && (
            <LibraryDetailModal
              inspiration={openInspiration}
              isDark={isDark}
              onClose={() => setOpenInspiration(null)}
            />
          )}
        </div>
      )}
    </Layout>
  );
}
